/**
 * CreateLookupDialog Component
 * Dialog for creating new lookup values
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LookupType, LookupValue } from '../types/configRegistryTypes';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { SearchableSelect } from 'components/common/SearchableSelect';

interface CreateLookupDialogProps {
  open: boolean;
  onClose: () => void;
  lookupType: LookupType | null;
  parentLookupOptions?: LookupValue[];
  selectedParentId?: string | null;
  onSuccess: (newLookup: LookupValue) => void;
}

export const CreateLookupDialog: React.FC<CreateLookupDialogProps> = ({
  open,
  onClose,
  lookupType,
  parentLookupOptions = [],
  selectedParentId = null,
  onSuccess,
}) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [parentLookup, setParentLookup] = useState<string>(selectedParentId || '');

  // Check if parent is required based on lookupType
  const requiresParent = lookupType?.dependsOn || lookupType?.parentTypeCode;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCode('');
      setDisplayName('');
      setDescription('');
      setParentLookup(selectedParentId || '');
      setError(null);
    }
  }, [open, selectedParentId]);

  // Auto-generate code from display name
  const handleDisplayNameChange = (value: string) => {
    setDisplayName(value);
    // Auto-generate code: uppercase, underscores instead of spaces
    const generatedCode = value.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
    setCode(generatedCode);
  };

  const handleSubmit = async () => {
    if (!rc || !lookupType) return;

    // Validation
    if (!code.trim()) {
      setError('Code is required');
      return;
    }
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }
    if (requiresParent && !parentLookup) {
      setError(`Parent ${lookupType.dependsOn || 'lookup'} is required`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await ConfigRegistryApis.createLookup(rc, {
        code: code.trim(),
        displayName: displayName.trim(),
        lookupType: lookupType.code,
        parentLookup: parentLookup || undefined,
        description: description.trim() || undefined,
      });

      console.log('Create lookup response:', response);

      // Check for error in response
      if (response.error) {
        const errorMsg = response.error?.message || response.error?.details || 'Failed to create lookup';
        setError(errorMsg);
        return;
      }

      // Handle response - API returns { data: {...}, message: "..." }
      // CMSApiClient wraps this as response.data = { data: {...}, message: "..." }
      const responseWrapper = response.data as any;
      const responseData = responseWrapper?.data || responseWrapper;

      if (responseData?.documentId) {
        const newLookup: LookupValue = {
          id: responseData.id || 0,
          documentId: responseData.documentId,
          code: responseData.code,
          displayName: responseData.displayName,
          parentValue: responseData.parentLookup?.documentId,
        };
        toast.success(`Successfully created "${responseData.displayName}" in ${lookupType.displayName}`);
        onSuccess(newLookup);
        onClose();
      } else {
        setError('Unexpected response format from server');
      }
    } catch (err: any) {
      console.error('Failed to create lookup:', err);
      setError(err?.message || 'Failed to create lookup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!lookupType) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          Create New {lookupType.displayName}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => handleDisplayNameChange(e.target.value)}
            required
            fullWidth
            size="small"
            placeholder="Enter display name..."
            autoFocus
          />

          <TextField
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            required
            fullWidth
            size="small"
            placeholder="AUTO_GENERATED_CODE"
            helperText="Unique identifier (auto-generated from display name)"
          />

          {requiresParent && parentLookupOptions.length > 0 && (
            <SearchableSelect
              fullWidth
              size="small"
              label={`Parent ${lookupType.dependsOn}`}
              value={parentLookup}
              onChange={setParentLookup}
              options={parentLookupOptions.map((opt) => ({ value: opt.documentId, label: opt.displayName }))}
            />
          )}

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Optional description..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !displayName.trim() || !code.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLookupDialog;

